import { inject, observer } from 'mobx-react';
import * as React from 'react';

import { AnalyticsStore, RouteStore } from 'bernie-core';
import { ClientLogger } from 'bernie-client';
import { ErrorBoundary } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';
import { performanceMetrics } from 'bernie-analytics';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';

import { CurrentHotelStore } from 'stores/current-hotel-store';

import { DateRange, RoomViewModel } from 'models';
import { DateType } from 'constants/dateType';

import { classNames } from 'uitk-react-utils';
import { UitkCard } from 'uitk-react-cards';

import { DateStepper } from 'components/infosite/rooms-and-rates/availability-wizard/internals/date-stepper';
import { SavingsOption } from 'components/infosite/rooms-and-rates/savings-option.tsx';
import { SearchValidation } from 'components/infosite/rooms-and-rates/availability-wizard/internals/search-validation';

const logger = ClientLogger.getLoggerWithIdentifier('availabilityWizard.error');

export type LocalizedAvailabilityWizardProps = LocalizedComponentProps & AvailabilityWizardProps;

export interface AvailabilityWizardProps {
  analytics?: AnalyticsStore;
  currentHotel?: CurrentHotelStore;
  currentHotelOnly?: boolean;
  loadingOffers: boolean;
  metaSavingsOption?: {
    alternateDateRange: DateRange;
    alternateDateMessage: string;
    url: string;
  };
  onUpdateResults: (event, searchCriteriaModel, goBack) => void;
  rooms?: RoomViewModel[];
  routeStore?: RouteStore;
  soldOut?: boolean;
}

export interface AvailabilityWizardState {
  inputErrors: AvailabilityWizardInputErrors;
  midnightBookingOffsetHours: number;
  numDaysBookableInAdvance: number;
}

export interface AvailabilityWizardInputErrors {
  adults?: string;
  children?: string;
  endDate?: string;
  startDate?: string;
}

@withLocalization
@inject('currentHotel', 'routeStore', 'analytics')
@observer
export class AvailabilityWizard extends React.Component<LocalizedAvailabilityWizardProps, AvailabilityWizardState> {
  private searchValidator: SearchValidation;

  constructor(props) {
    super(props);
    const { l10n } = props;
    this.searchValidator = new SearchValidation(l10n.formatText);
    this.handleChange = this.handleChange.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.state = { inputErrors: {}, midnightBookingOffsetHours: 4, numDaysBookableInAdvance: 500 };
  }

  private handleChange(event, isTriggeredByStepper) {
    let target = event.target;
    const offerSearchData = {};

    if (!Array.isArray(target)) {
      target = [target];
    }

    target.forEach(searchChangeProp => {
      offerSearchData[searchChangeProp.name] = searchChangeProp.value;
    });
    this.props.currentHotel.setOfferData(offerSearchData);
    this.handleSubmit(isTriggeredByStepper);
  }

  private logTracking(rfrr, linkName) {
    const { analytics } = this.props;
    if (analytics && typeof analytics.clickTracker === 'function') {
      this.props.analytics.clickTracker(rfrr, linkName, null, null)();
    }
  }

  private handleSubmit(isTriggeredByStepper = false) {
    const { currentHotel } = this.props;
    const errorState = {
      inputErrors: {},
    };

    const startDateError = this.searchValidator.startDate(currentHotel.offerSearchData);
    const endDateError = this.searchValidator.endDate(currentHotel.offerSearchData);
    const adultError = this.searchValidator.adults(currentHotel.offerSearchData);
    const childrenError = this.searchValidator.children(currentHotel.offerSearchData);

    if (startDateError || endDateError || adultError || childrenError) {
      errorState.inputErrors = {
        startDate: startDateError,
        endDate: endDateError,
        adults: adultError,
        children: childrenError,
      };
    }

    if (this.hasInputErrors(errorState)) {
      if (currentHotel.offers && currentHotel.offers.recommendedOffer) {
        currentHotel.clearRecommendedOfferFromOffers();
      }
      if (currentHotel.offers && currentHotel.offers.rooms && currentHotel.offers.rooms.length > 0) {
        currentHotel.clearRoomsFromOffers();
      }
      if (!isTriggeredByStepper) {
        this.props.routeStore.goBack();
      }
      const errorMessages = [];
      const errors = errorState.inputErrors;
      for (const i in errors) {
        if (errors.hasOwnProperty(i) && errors[i] !== undefined) {
          errorMessages.push(errors[i]);
        }
      }
      if (errorMessages.length > 0) {
        logger.logEvent(new SystemEvent(SystemEventLevel.WARN, 'SEARCH_FORM_HANDLE_SUBMIT'), JSON.stringify(errorMessages));
      }
    } else {
      performanceMetrics.reset();
      const { onUpdateResults } = this.props;
      if (onUpdateResults && typeof onUpdateResults === 'function') {
        if (isTriggeredByStepper) {
          onUpdateResults(null, currentHotel.offerSearchData, false);
          this.logTracking('ChangeDatesStepper:Submit', 'Submitsearch using date stepper buttons');
        } else {
          onUpdateResults(null, currentHotel.offerSearchData, true);
          this.logTracking('FORM.SUBMIT', 'Submit search form');
        }
      }
    }
    this.setState(errorState);
  }

  private hasInputErrors(validationItems) {
    const temp = validationItems ? validationItems : {};
    for (const key in temp.inputErrors) {
      if (temp.inputErrors[key] !== undefined) {
        return true;
      }
    }
  }

  public render() {
    const { currentHotel, l10n, loadingOffers, metaSavingsOption } = this.props;

    const { offerSearchData } = currentHotel;

    const { inputErrors, midnightBookingOffsetHours, numDaysBookableInAdvance } = this.state;
    const { formatText } = l10n;

    const startDate = offerSearchData && offerSearchData.startDate;
    const endDate = offerSearchData && offerSearchData.endDate;
    const buttonText = formatText('hotels.infosite.searchForm.submit.label');
    const dateStepperClasses = classNames('uitk-type-center', {
      'all-t-padding-three': metaSavingsOption,
      'all-y-padding-three': !metaSavingsOption,
    });
    const stepperProps = {
      clickHandler: this.handleChange,
      endDate,
      loadingOffers,
      startDate,
      buttonText,
      invalid: inputErrors && inputErrors.startDate,
      midnightBookingOffsetHours,
      numDaysBookableInAdvance,
    };

    return (
      <div className="l-x-padding-zero m-x-padding-zero">
        <ErrorBoundary label="hotel.information.rooms.availabilityWizardStepper">
          <div className="uitk-grid all-b-padding-three">
            <div className="uitk-cell">
              <UitkCard className={dateStepperClasses}>
                <DateStepper {...stepperProps} dateType={DateType.CHECK_IN} />
                <DateStepper {...stepperProps} dateType={DateType.CHECK_OUT} />
                {metaSavingsOption && (
                  <div id="meta-savings-message" className="uitk-cell all-t-margin-one">
                    <div className="uitk-card-separator-top">
                      <SavingsOption dateOption={metaSavingsOption} />
                    </div>
                  </div>
                )}
              </UitkCard>
            </div>
          </div>
        </ErrorBoundary>
      </div>
    );
  }
}
